/**
 * Per-tab persistence for the Conversations panel.
 *
 * Moving between routes in the SPA keeps React state, but a full reload or a
 * hard navigation (an email link, an external link, the admin panel) throws it
 * away — the panel snaps shut and whatever was half-typed is gone.
 *
 * sessionStorage rather than localStorage on purpose: this is "what I was doing
 * in *this* tab". Two tabs open on different conversations should not fight over
 * one key, and the panel should not reopen days later in a new session.
 *
 * Every accessor is defensive. sessionStorage throws in Safari private mode and
 * when a storage quota is hit, and none of this is important enough to take the
 * page down with it.
 */

const KEY = 'cgs.chat.session';

function read() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function write(state) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode or quota. Persistence is an enhancement, not a requirement.
  }
}

export function loadChatSession() {
  const state = read();
  return {
    isPanelOpen: !!state.isPanelOpen,
    activeConversationId: state.activeConversationId ?? null,
    drafts: state.drafts && typeof state.drafts === 'object' ? state.drafts : {},
  };
}

export function saveChatSession(patch) {
  write({ ...read(), ...patch });
}

/**
 * Composer text, kept per conversation so switching between two half-written
 * replies does not lose either.
 */
export function saveDraft(conversationId, text) {
  if (!conversationId) return;
  const state = read();
  const drafts = { ...(state.drafts || {}) };

  if (text && text.trim()) {
    drafts[conversationId] = text;
  } else {
    // An emptied box means the member cleared it; do not resurrect old text.
    delete drafts[conversationId];
  }

  // Bound the map so a member who opens many conversations does not accumulate
  // drafts forever in a fixed-size store.
  const keys = Object.keys(drafts);
  if (keys.length > 20) delete drafts[keys[0]];

  write({ ...state, drafts });
}

export function loadDraft(conversationId) {
  if (!conversationId) return '';
  return read().drafts?.[conversationId] || '';
}

export function clearChatSession() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do.
  }
}

export default { loadChatSession, saveChatSession, saveDraft, loadDraft, clearChatSession };
